import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, Search, Trash2, RotateCcw, ShieldAlert, MapPin, MessageSquare, Users } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';
function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${BACKEND_URL}/api/files/${p}`;
}

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [status, setStatus] = useState('active');
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  useEffect(() => {
    api.get('/admin/me')
      .then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try { setStats((await api.get('/admin/users/stats')).data); } catch {}
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: String(PAGE), offset: String(offset) });
      if (search.trim()) params.set('search', search.trim());
      if (city.trim()) params.set('city', city.trim());
      const r = await api.get(`/admin/users?${params}`);
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, city, status, offset]);

  useEffect(() => { if (authChecked) { loadStats(); loadUsers(); } }, [authChecked, loadStats, loadUsers]);

  const remove = async (u) => {
    const reason = window.prompt(`Remove ${u.display_name || u.email}? Reason (optional):`);
    if (reason === null) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`, { data: { reason } });
      toast.success('Profile removed');
      loadUsers(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/reactivate`);
      toast.success('Profile reactivated');
      loadUsers(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]"/></div>;
  }

  return (
    <div className="min-h-screen bg-white pb-12" data-testid="admin-users-page">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Users</h1>
        <Link to="/admin/support" className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#4B4B52] hover:text-[#B91C1C] transition" data-testid="link-admin-support">
          <MessageSquare size={11}/> Tickets
        </Link>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11}/> Admin
        </span>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="px-5 pt-4 grid grid-cols-3 gap-2">
          <StatCard label="Active" value={stats.total_active} icon={Users} color="#B91C1C"/>
          <StatCard label="Incomplete" value={stats.incomplete} icon={Loader2} color="#B38138"/>
          <StatCard label="Removed" value={stats.deleted} icon={Trash2} color="#8A8A93"/>
        </div>
      )}

      {/* Top cities */}
      {stats?.top_cities?.length > 0 && (
        <div className="px-5 mt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] mb-2">Top cities</p>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2">
            {stats.top_cities.map(c => (
              <button key={c.city} onClick={() => { setCity(c.city); setOffset(0); }} data-testid={`top-city-${c.city}`}
                className="flex-shrink-0 px-3 py-1.5 rounded-full bg-[var(--surface-2)] border border-[var(--hairline)] text-[11px] font-semibold text-[#4B4B52] hover:bg-[#B91C1C]/[0.06] hover:text-[#B91C1C] hover:border-[#B91C1C]/20 transition inline-flex items-center gap-1.5">
                <MapPin size={10}/> {c.city}
                <span className="text-[10px] font-bold text-[#8A8A93]">{c.count.toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search + status pills */}
      <div className="px-5 mt-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]"/>
            <input value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }}
              placeholder="Name, email, or user_id" data-testid="admin-users-search"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"/>
          </div>
          <input value={city} onChange={e => { setCity(e.target.value); setOffset(0); }}
            placeholder="City" data-testid="admin-users-city"
            className="w-32 h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"/>
        </div>
        <div className="flex gap-2">
          {[['active','Active'],['deleted','Removed'],['incomplete','Incomplete'],['all','All']].map(([k,l]) => (
            <button key={k} onClick={() => { setStatus(k); setOffset(0); }} data-testid={`admin-users-status-${k}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                status === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'
              }`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Total count */}
      <p className="px-5 mt-3 text-[11px] text-[#8A8A93]">Showing {users.length} of {total.toLocaleString()} users</p>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]"/></div>
      ) : users.length === 0 ? (
        <div className="text-center px-6 py-16 text-[#8A8A93]">No users match these filters.</div>
      ) : (
        <div className="px-3 mt-2 divide-y divide-[var(--hairline)]" data-testid="admin-users-list">
          {users.map(u => {
            const isDeleted = !!u.deleted_at;
            return (
              <div key={u.user_id} className="px-3 py-3 flex items-center gap-3 hover:bg-black/[0.02] transition cursor-pointer" data-testid={`user-row-${u.user_id}`}
                   onClick={() => navigate(`/admin/users/${u.user_id}`)}>
                <img src={getPhotoUrl(u.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.user_id}`}
                  alt="" className="w-12 h-12 rounded-full object-cover bg-[var(--surface-2)] flex-shrink-0"
                  onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.user_id}`; }}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold text-[14px] truncate ${isDeleted ? 'line-through text-[#8A8A93]' : 'text-[#0F0F10]'}`}>
                      {u.display_name || '(no name)'}{u.age ? `, ${u.age}` : ''}
                    </p>
                    {isDeleted && <span className="badge bg-black/[0.06] text-[#4B4B52] border-black/10">Removed</span>}
                    {!u.profile_complete && !isDeleted && <span className="badge bg-[#FDF7EC] text-[#B38138] border-[#B38138]/20">Incomplete</span>}
                    {(u.report_count || 0) > 0 && <span className="badge bg-[#FEF2F2] text-[#B91C1C] border-[#B91C1C]/20">⚠ {u.report_count} reports</span>}
                  </div>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5 truncate">{u.email || '—'} · {u.location?.city || 'no city'} · joined {relativeTime(u.created_at)}</p>
                </div>
                {isDeleted ? (
                  <button onClick={(e) => { e.stopPropagation(); reactivate(u); }} data-testid={`reactivate-${u.user_id}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold border border-[#15803D]/30 text-[#15803D] hover:bg-[#15803D]/[0.06] inline-flex items-center gap-1 transition">
                    <RotateCcw size={11}/> Reactivate
                  </button>
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); remove(u); }} data-testid={`delete-${u.user_id}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold border border-[#B91C1C]/30 text-[#B91C1C] hover:bg-[#B91C1C]/[0.06] inline-flex items-center gap-1 transition">
                    <Trash2 size={11}/> Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE && (
        <div className="px-5 mt-5 flex items-center justify-between">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="px-4 py-2 rounded-full border border-[var(--hairline-strong)] text-[12px] font-semibold disabled:opacity-30 hover:bg-black/[0.02] transition">← Prev</button>
          <span className="text-[11px] text-[#8A8A93]">{offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}</span>
          <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}
            className="px-4 py-2 rounded-full border border-[var(--hairline-strong)] text-[12px] font-semibold disabled:opacity-30 hover:bg-black/[0.02] transition">Next →</button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-[var(--surface-2)] rounded-2xl p-3 border border-[var(--hairline)]" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} style={{ color }}/>
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{label}</p>
      </div>
      <p className="font-display text-xl font-[700] tabular-nums" style={{ color }}>
        {(value || 0).toLocaleString()}
      </p>
    </div>
  );
}
